import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import { usePermission } from '../context/PermissionContext';
import { surveyJobBriefOptions, itsqfOptionsForSurvey, skillBriefOptionsForSurvey, findSurveyOption } from '../utils/jobOptions';

const fmtN = (v) => { const n = String(v).replace(/[^0-9]/g,''); return n ? Number(n).toLocaleString() : ''; };
const pN = (v) => parseInt(String(v).replace(/[^0-9]/g,'')) || 0;

export default function ServicePackageNewPage() {
  const navigate = useNavigate();
  const { canPerformAction } = usePermission();
  const canEdit = canPerformAction('서비스 패키지 등록/편집');
  const { id } = useParams();
  const isEdit = !!id;
  const [info, setInfo] = useState({ name:'', category:'', notes:'' });
  const [tasks, setTasks] = useState([{ task_name:'', survey_job_id:null, itsqf_job_id:null, skill_level:'', default_monthly_rate:'', default_headcount:'1', default_mm:'1' }]);
  const [surveyJobs, setSurveyJobs] = useState([]);
  const [saving, setSaving] = useState(false);

  const surveyOptions = useMemo(() => surveyJobBriefOptions(surveyJobs), [surveyJobs]);

  useEffect(() => {
    api.get('/survey-jobs').then(setSurveyJobs).catch(()=>{});
    if (isEdit) {
      api.get(`/service-packages/${id}`).then(p => {
        setInfo({ name:p.name||'', category:p.category||'', notes:p.notes||'' });
        setTasks((p.tasks||[]).map(t => ({
          task_name: t.task_name||'',
          survey_job_id: t.survey_job_id || null,
          itsqf_job_id: t.itsqf_job_id,
          skill_level: t.skill_level || '',
          default_monthly_rate: String(t.default_monthly_rate || ''),
          default_headcount: String(t.default_headcount||1),
          default_mm: String(t.default_mm||0),
        })));
        if ((p.tasks||[]).length === 0) setTasks([{ task_name:'', survey_job_id:null, itsqf_job_id:null, skill_level:'', default_monthly_rate:'', default_headcount:'1', default_mm:'1' }]);
      }).catch(e=>alert(e.message));
    }
  }, [id, isEdit]);

  const upd = (i, key, val) => setTasks(tasks.map((t,j) => j===i ? {...t,[key]:val} : t));
  const move = (i, dir) => {
    const u = [...tasks]; const j = i+dir;
    if (j < 0 || j >= u.length) return;
    [u[i], u[j]] = [u[j], u[i]]; setTasks(u);
  };
  const add = () => setTasks([...tasks, { task_name:'', survey_job_id:null, itsqf_job_id:null, skill_level:'', default_monthly_rate:'', default_headcount:'1', default_mm:'1' }]);
  const del = (i) => setTasks(tasks.filter((_,j)=>j!==i));

  // 조사용직무 변경 시 ITSQF/등급/단가 모두 리셋
  const setSurvey = (i, surveyJobId) => {
    setTasks(tasks.map((t,j) => j===i ? { ...t, survey_job_id: surveyJobId || null, itsqf_job_id:null, skill_level:'', default_monthly_rate:'' } : t));
  };
  // ITSQF 직무 변경 (등급은 유지 — 같은 survey_job 의 rate 테이블 공유)
  const setItsqf = (i, itsqfJobId) => {
    setTasks(tasks.map((t,j) => j===i ? { ...t, itsqf_job_id: itsqfJobId || null } : t));
  };
  // 등급 변경 시 단가 자동 채움
  const setSkillLevel = (i, skillLevel) => {
    const t = tasks[i];
    const opt = findSurveyOption(surveyOptions, t.survey_job_id);
    const rate = opt?.rates?.find(r => r.skill_level === skillLevel);
    setTasks(tasks.map((tt,j) => j===i ? {
      ...tt,
      skill_level: skillLevel,
      default_monthly_rate: rate ? String(rate.monthly_rate || 0) : tt.default_monthly_rate,
    } : tt));
  };

  const save = async () => {
    if (!info.name.trim()) { alert('패키지 이름은 필수입니다.'); return; }
    const cleaned = tasks
      .filter(t => (t.task_name||'').trim())
      .map(t => ({
        task_name: t.task_name.trim(),
        itsqf_job_id: t.itsqf_job_id || null,
        skill_level: t.skill_level || null,
        default_monthly_rate: pN(t.default_monthly_rate),
        default_headcount: parseInt(t.default_headcount)||1,
        default_mm: parseFloat(t.default_mm)||0,
      }));
    if (cleaned.length === 0) { alert('task 를 1개 이상 입력하세요.'); return; }
    setSaving(true);
    try {
      const body = { name: info.name.trim(), category: info.category.trim()||null, notes: info.notes||null, tasks: cleaned };
      if (isEdit) await api.put(`/service-packages/${id}`, body);
      else await api.post('/service-packages', body);
      navigate('/services');
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="card">
        <div className="card-title mb-8">서비스 패키지 {isEdit?'수정':'등록'}</div>
        <div className="form-group">
          <label className="form-label">패키지 이름 *</label>
          <input className="form-input" value={info.name} onChange={e=>setInfo({...info,name:e.target.value})} placeholder="예: ISMS-P 컨설팅" />
        </div>
        <div className="form-group">
          <label className="form-label">분류</label>
          <input className="form-input" value={info.category} onChange={e=>setInfo({...info,category:e.target.value})} placeholder="예: 보안컨설팅" />
        </div>
        <div className="form-group">
          <label className="form-label">메모</label>
          <textarea className="form-input" rows={2} value={info.notes} onChange={e=>setInfo({...info,notes:e.target.value})} />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Task 목록</div>
          <button className="btn btn-outline btn-sm" onClick={add}>+ Task 추가</button>
        </div>
        <table>
          <thead>
            <tr>
              <th style={{width:30}}></th>
              <th>품명 (다중 라인)</th>
              <th style={{width:130}}>조사용직무</th>
              <th style={{width:140}}>ITSQF 직무</th>
              <th style={{width:110}}>인력등급</th>
              <th style={{width:60}}>인원</th>
              <th style={{width:80}}>투입(M/M)</th>
              <th style={{width:120,textAlign:'right'}}>월단가</th>
              <th style={{width:80}}></th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t,i) => {
              const sOpt = findSurveyOption(surveyOptions, t.survey_job_id);
              const itsqfOpts = itsqfOptionsForSurvey(sOpt);
              const skillOpts = skillBriefOptionsForSurvey(sOpt);
              return (
                <tr key={i}>
                  <td style={{textAlign:'center',color:'#94a3b8',fontSize:11}}>{i+1}</td>
                  <td>
                    <textarea className="form-input" rows={3} style={{fontSize:11,padding:'5px 8px'}}
                      value={t.task_name} onChange={e=>upd(i,'task_name',e.target.value)}
                      placeholder={'▶ 항목 제목\n - 세부설명\n - 세부설명'} />
                  </td>
                  <td>
                    <select className="form-input" style={{padding:'5px 6px',fontSize:11}} value={t.survey_job_id||''} onChange={e=>setSurvey(i, e.target.value?parseInt(e.target.value):null)}>
                      <option value="">— 선택 —</option>
                      {surveyOptions.map(o => <option key={o.survey_job_id} value={o.survey_job_id}>{o.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-input" style={{padding:'5px 6px',fontSize:11}} value={t.itsqf_job_id||''} onChange={e=>setItsqf(i, e.target.value?parseInt(e.target.value):null)} disabled={!sOpt}>
                      <option value="">— 선택 —</option>
                      {itsqfOpts.map(o => <option key={o.itsqf_job_id} value={o.itsqf_job_id}>{o.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-input" style={{padding:'5px 6px',fontSize:11}} value={t.skill_level||''} onChange={e=>setSkillLevel(i, e.target.value)} disabled={!sOpt}>
                      <option value="">— 선택 —</option>
                      {skillOpts.map(s => <option key={s.skill_level} value={s.skill_level}>{s.label}</option>)}
                    </select>
                  </td>
                  <td><input className="form-input" style={{padding:'5px 8px',textAlign:'center'}} value={t.default_headcount} onChange={e=>upd(i,'default_headcount',e.target.value)} /></td>
                  <td><input className="form-input" style={{padding:'5px 8px',textAlign:'center'}} value={t.default_mm} onChange={e=>upd(i,'default_mm',e.target.value)} /></td>
                  <td><input className="form-input" style={{padding:'5px 8px',textAlign:'right'}} value={fmtN(t.default_monthly_rate)} onChange={e=>upd(i,'default_monthly_rate',e.target.value)} placeholder="자동" /></td>
                  <td>
                    <button className="btn btn-outline btn-sm" style={{padding:'2px 6px'}} onClick={()=>move(i,-1)} disabled={i===0}>↑</button>
                    <button className="btn btn-outline btn-sm" style={{padding:'2px 6px',marginLeft:2}} onClick={()=>move(i,1)} disabled={i===tasks.length-1}>↓</button>
                    <button className="btn btn-danger btn-sm" style={{padding:'2px 6px',marginLeft:2}} onClick={()=>del(i)}>✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="text-muted" style={{fontSize:11,marginTop:8}}>월단가는 ITSQF 직무 → 기술등급 선택 시 자동으로 채워지며, 패키지별로 수정 가능합니다.</div>
      </div>

      <div style={{display:'flex',gap:8,marginTop:16}}>
        {canEdit && <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'저장 중...':'저장'}</button>}
        <button className="btn btn-outline" onClick={()=>navigate('/services')}>{canEdit ? '취소' : '목록'}</button>
      </div>
    </div>
  );
}
